import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { 
  Map, Compass, User, LogOut, Crown, ChevronRight, Mail, Beer, Camera, Loader2, 
  Pencil, Home, X, Route, MapPin, Trophy, CheckCircle2, Share2, Calendar, Shield
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";

const LOGO_URL = "https://res.cloudinary.com/dmivaa0e0/image/upload/v1772834626/branding/logo_badge.png";

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editPicture, setEditPicture] = useState("");
  const [editHomeLocation, setEditHomeLocation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile data
  const [profileData, setProfileData] = useState(null);
  const [completedRides, setCompletedRides] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [activeTab, setActiveTab] = useState("stats"); // stats, rides, checkins
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    if (user?.user_id) {
      fetchProfileData();
    }
  }, [user?.user_id]);

  const fetchProfileData = async () => {
    setLoadingProfile(true);
    try {
      const [profileRes, ridesRes, checkinsRes] = await Promise.all([
        axios.get(`${API}/leaderboard/user/${user.user_id}/profile`),
        axios.get(`${API}/leaderboard/user/${user.user_id}/completed-rides`),
        axios.get(`${API}/leaderboard/user/${user.user_id}/checkins`)
      ]);
      setProfileData(profileRes.data);
      setCompletedRides(ridesRes.data.completed_rides);
      setCheckins(checkinsRes.data.checkins);
    } catch (error) {
      console.error("Failed to load profile data:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      toast.success("Ride safe!");
      navigate('/');
    } catch (error) {
      toast.error("Logout failed");
    } finally {
      setLoggingOut(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'R';
  };

  const openEditModal = () => {
    setEditUsername(user?.username || "");
    setEditPicture(user?.picture || "");
    setEditHomeLocation(user?.home_location || null);
    setShowEditModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const sigResponse = await axios.get(`${API}/uploads/signature?folder=profiles`);
      const sig = sigResponse.data;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );

      if (!uploadResponse.ok) throw new Error("Upload failed");

      const result = await uploadResponse.json();
      setEditPicture(result.secure_url);
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (editUsername && !/^[a-zA-Z0-9_]{3,20}$/.test(editUsername)) {
      toast.error("Username must be 3-20 characters (letters, numbers, underscores)");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/auth/me`, {
        username: editUsername || null,
        picture: editPicture || null,
        home_location: editHomeLocation
      });
      
      await refreshUser();
      await fetchProfileData();
      setShowEditModal(false);
      toast.success("Profile updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/rider/${user?.user_id}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${user?.name}'s Rider Profile`,
          text: `Check out my rides and dives stats!`,
          url: shareUrl
        });
      } catch (e) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Profile link copied to clipboard!");
    }
  };

  const getCategoryEmoji = (category) => {
    switch (category) {
      case 'dive_bar': return '🍺';
      case 'restaurant': return '🍔';
      case 'hotel': return '🏨';
      case 'gas_station': return '⛽';
      case 'city': return '🏙️';
      case 'landmark': return '🗿';
      default: return '📍';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const stats = profileData?.stats || {};

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-24">
      {/* Header */}
      <div className="p-4 pt-6">
        {/* User Card with Stats */}
        <div className="glass rounded-xl p-5 mb-4">
          <div className="flex items-start gap-4 mb-4">
            <Avatar className="w-20 h-20 border-2 border-bronze">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-bronze/20 text-bronze font-oswald text-2xl">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="font-oswald text-xl font-bold uppercase">{user?.name}</h2>
              {user?.username && (
                <p className="text-bronze text-sm">@{user.username}</p>
              )}
              {user?.home_location && (
                <div className="flex items-center gap-1 text-green-500 text-xs mt-1">
                  <Home className="w-3 h-3" />
                  <span className="truncate">{user.home_location.name}</span>
                </div>
              )}
              {user?.is_premium && (
                <div className="flex items-center gap-1 mt-2">
                  <Crown className="w-4 h-4 text-yellow-500" />
                  <span className="text-yellow-500 text-xs font-bold uppercase">Premium</span>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-bronze/20"
                onClick={openEditModal}
              >
                <Pencil className="w-5 h-5 text-bronze" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full hover:bg-cyan-500/20"
                onClick={handleShare}
              >
                <Share2 className="w-5 h-5 text-cyan-400" />
              </Button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <Route className="w-5 h-5 text-green-400 mx-auto mb-1" />
              <div className="text-lg font-oswald font-bold">{stats.total_miles || 0}</div>
              <div className="text-[10px] text-gray-500 uppercase">Miles</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <Trophy className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
              <div className="text-lg font-oswald font-bold">{stats.total_rides || 0}</div>
              <div className="text-[10px] text-gray-500 uppercase">Rides</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <Beer className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <div className="text-lg font-oswald font-bold">{stats.total_bars || 0}</div>
              <div className="text-[10px] text-gray-500 uppercase">Dives</div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <CheckCircle2 className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
              <div className="text-lg font-oswald font-bold">{stats.total_checkins || 0}</div>
              <div className="text-[10px] text-gray-500 uppercase">Check-ins</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === "rides" ? "default" : "outline"}
            className={`flex-1 h-10 font-oswald uppercase text-sm ${activeTab === "rides" ? "bg-bronze" : "border-white/20"}`}
            onClick={() => setActiveTab("rides")}
          >
            <Map className="w-4 h-4 mr-1" />
            Rides ({completedRides.length})
          </Button>
          <Button
            variant={activeTab === "checkins" ? "default" : "outline"}
            className={`flex-1 h-10 font-oswald uppercase text-sm ${activeTab === "checkins" ? "bg-bronze" : "border-white/20"}`}
            onClick={() => setActiveTab("checkins")}
          >
            <MapPin className="w-4 h-4 mr-1" />
            Check-ins ({checkins.length})
          </Button>
        </div>

        {/* Content */}
        {loadingProfile ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-bronze" />
          </div>
        ) : (
          <>
            {/* Completed Rides */}
            {activeTab === "rides" && (
              <div className="space-y-3">
                {completedRides.length === 0 ? (
                  <div className="glass rounded-xl p-6 text-center">
                    <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No completed rides yet</p>
                    <p className="text-gray-600 text-sm mt-1">Start a ride to track your adventures!</p>
                  </div>
                ) : (
                  completedRides.map((ride) => (
                    <div 
                      key={ride.ride_id} 
                      className="glass rounded-xl overflow-hidden cursor-pointer hover:border-bronze/50 transition-colors"
                      onClick={() => navigate(`/trip/${ride.trip_id}`)}
                    >
                      {ride.cover_image && (
                        <div className="h-24 overflow-hidden">
                          <img src={ride.cover_image} alt={ride.trip_title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-4">
                        <h3 className="font-oswald font-bold text-lg mb-2">{ride.trip_title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <div className="flex items-center gap-1 text-green-400">
                            <Route className="w-4 h-4" />
                            <span>{Math.round(ride.miles_logged)} mi</span>
                          </div>
                          <div className="flex items-center gap-1 text-cyan-400">
                            <MapPin className="w-4 h-4" />
                            <span>{ride.stops_completed} stops</span>
                          </div>
                          <div className="flex items-center gap-1 text-amber-400">
                            <Beer className="w-4 h-4" />
                            <span>{ride.bars_visited} dives</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500 text-xs mt-2">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(ride.completed_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Check-ins */}
            {activeTab === "checkins" && (
              <div className="space-y-2">
                {checkins.length === 0 ? (
                  <div className="glass rounded-xl p-6 text-center">
                    <MapPin className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No check-ins yet</p>
                    <p className="text-gray-600 text-sm mt-1">Check in at stops during your rides!</p>
                  </div>
                ) : (
                  checkins.map((checkin, idx) => (
                    <div 
                      key={checkin.checkin_id || idx}
                      className={`glass rounded-xl p-4 ${checkin.is_bar ? 'border-amber-500/30 bg-amber-500/5' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${checkin.is_bar ? 'bg-amber-500/20' : 'bg-white/10'}`}>
                          {getCategoryEmoji(checkin.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold truncate">{checkin.stop_name}</span>
                            {checkin.is_bar && (
                              <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full uppercase font-bold">Bar</span>
                            )}
                          </div>
                          {checkin.address && (
                            <p className="text-xs text-gray-500 truncate">{checkin.address}</p>
                          )}
                          <p className="text-xs text-gray-600 mt-1">{formatDate(checkin.timestamp)}</p>
                        </div>
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}

        {/* Menu Items */}
        <div className="space-y-2 mt-6">
          {user?.is_admin && (
            <div
              className="glass rounded-xl p-4 flex items-center justify-between cursor-pointer hover:border-red-500/50 transition-colors border border-red-500/30"
              onClick={() => navigate('/admin')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="font-medium">Admin Dashboard</p>
                  <p className="text-xs text-gray-500">Manage users & submissions</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </div>
          )}

          {!user?.is_premium && (
            <div
              className="glass rounded-xl p-4 flex items-center justify-between cursor-pointer hover:border-bronze/50 transition-colors"
              onClick={() => navigate('/subscription')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="font-medium">Go Premium</p>
                  <p className="text-xs text-gray-500">Unlock private routes & submissions</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </div>
          )}

          <div
            className="glass rounded-xl p-4 flex items-center justify-between cursor-pointer hover:border-red-500/30 transition-colors"
            onClick={handleLogout}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <LogOut className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="font-medium">Sign Out</p>
                <p className="text-xs text-gray-500">Until next ride</p>
              </div>
            </div>
            {loggingOut && (
              <Loader2 className="w-5 h-5 animate-spin" />
            )}
          </div>
        </div>

        {/* App Info */}
        <div className="mt-8 text-center">
          <div className="flex justify-center mb-3">
            <img src={LOGO_URL} alt="Rides & Dives" className="w-1/3 max-w-[150px] h-auto opacity-50" />
          </div>
          <p className="text-xs text-gray-600">Version 1.0.0</p>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-oswald text-xl uppercase">Edit Profile</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-5 py-4">
            {/* Photo Upload */}
            <div className="flex flex-col items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <div 
                className="relative cursor-pointer group"
                onClick={() => fileInputRef.current?.click()}
              >
                <Avatar className="w-24 h-24 border-2 border-bronze">
                  <AvatarImage src={editPicture} />
                  <AvatarFallback className="bg-bronze/20 text-bronze font-oswald text-2xl">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Tap to change photo</p>
            </div>

            {/* Username */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-400 uppercase tracking-wide">Username</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                <Input
                  data-testid="edit-username-input"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="your_username"
                  className="h-12 bg-[#121212] border-white/10 focus:border-bronze pl-8"
                  maxLength={20}
                />
              </div>
            </div>

            {/* Home Location */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-400 uppercase tracking-wide flex items-center gap-2">
                <Home className="w-3 h-3" />
                Home Base
              </Label>
              <PlaceAutocomplete
                onPlaceSelect={(place) => setEditHomeLocation(place)}
                placeholder="Your starting point for rides..."
                value={editHomeLocation?.name || ""}
              />
              {editHomeLocation && (
                <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate">{editHomeLocation.name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditHomeLocation(null)}
                    className="text-gray-500 hover:text-red-500 transition-colors ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveProfile}
              disabled={saving || uploading}
              className="w-full h-12 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <div className="bottom-nav glass safe-bottom">
        <div className="flex justify-around py-3">
          <button
            data-testid="nav-dashboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            <Map className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">My Trips</span>
          </button>
          <button
            data-testid="nav-discover"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/discover')}
          >
            <Beer className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Discover</span>
          </button>
          <button
            data-testid="nav-leaderboard"
            className="flex flex-col items-center gap-1 text-gray-500 hover:text-white transition-colors"
            onClick={() => navigate('/leaderboard')}
          >
            <Trophy className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Ranks</span>
          </button>
          <button
            data-testid="nav-profile"
            className="flex flex-col items-center gap-1 text-white"
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-wide">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
